import { useState } from "react";
import { Box, Stack, Tooltip, Typography } from "@mui/material";
import InfoIcon from "@mui/icons-material/InfoOutlined";
import LiveIcon from "@mui/icons-material/ShowChartRounded";
import BookedIcon from "@mui/icons-material/InventoryRounded";

import Card3D from "./Card3D";
import { money, tone } from "../api";
import type { BotStats, Trade } from "../api";

interface PnLCardProps {
  stats: BotStats;
  open: Trade[];
  clock: string;
}

/** Premium actually at risk right now: entry premium x quantity, per open leg. */
function capitalDeployed(open: Trade[]): number {
  return open.reduce((sum, t) => sum + t.entry_price * t.qty, 0);
}

export default function PnLCard({ stats, open, clock }: PnLCardProps) {
  const [view, setView] = useState<"total" | "booked">("total");

  const headline = view === "total" ? stats.total_pnl : stats.realised_pnl;
  const t = tone(headline);
  const txns = stats.open_count + stats.closed_count;
  const activeAlgos = new Set(open.map((o) => o.strategy)).size;

  return (
    <Card3D intensity={4}>
      <Box sx={{ p: 2.5 }}>
        <Stack direction="row" alignItems="center" gap={1} className="layer-1">
          <Typography sx={{ fontSize: "1.02rem", fontWeight: 800 }}>
            Overall Profit/Loss
          </Typography>
          <Tooltip
            title={
              view === "total"
                ? "Booked P&L plus mark-to-market on open legs."
                : "Realised P&L from closed trades only."
            }
          >
            <InfoIcon sx={{ fontSize: 15, color: "var(--muted)" }} />
          </Tooltip>

          <Stack
            direction="row"
            sx={{
              ml: "auto",
              p: 0.4,
              gap: 0.4,
              borderRadius: "9px",
              background: "#0a121c",
              border: "1px solid var(--border)",
            }}
          >
            {(
              [
                { k: "total", label: "Total", icon: <LiveIcon /> },
                { k: "booked", label: "Booked", icon: <BookedIcon /> },
              ] as const
            ).map((b) => (
              <Stack
                key={b.k}
                direction="row"
                alignItems="center"
                gap={0.6}
                onClick={() => setView(b.k)}
                sx={{
                  px: 1.3,
                  py: 0.5,
                  borderRadius: "7px",
                  cursor: "pointer",
                  fontSize: ".76rem",
                  fontWeight: 800,
                  color: view === b.k ? "#04121f" : "var(--muted)",
                  background: view === b.k ? "#e8f1fb" : "transparent",
                  boxShadow: view === b.k ? "0 2px 8px rgba(0,0,0,.4)" : "none",
                  transition: "all 150ms ease",
                  "& svg": { fontSize: 15 },
                }}
              >
                {b.icon}
                {b.label}
              </Stack>
            ))}
          </Stack>
        </Stack>

        <Typography
          className={`mono ${t} ${t === "up" ? "glow-green" : t === "down" ? "glow-red" : ""} layer-2`}
          sx={{ fontSize: "2.5rem", fontWeight: 900, mt: 1.5, lineHeight: 1.05 }}
        >
          {money(headline, true)}
        </Typography>

        <Typography
          sx={{
            fontSize: ".8rem",
            color: "var(--muted)",
            textDecoration: "underline",
            textUnderlineOffset: 3,
            mt: 0.4,
          }}
        >
          in {txns} transaction{txns === 1 ? "" : "s"}
        </Typography>

        <Typography sx={{ fontSize: ".76rem", color: "var(--muted)", mt: 1.2 }}>
          Last updated:{" "}
          <b style={{ color: "var(--text)" }}>
            {new Date().toLocaleDateString("en-IN", {
              day: "numeric",
              month: "short",
              year: "numeric",
            })}
          </b>{" "}
          | <span className="mono">{clock}</span> IST
        </Typography>

        <Stack direction="row" gap={1.2} flexWrap="wrap" sx={{ mt: 2 }}>
          <Box className="tile3d" sx={{ flex: "1 1 132px" }}>
            <Typography sx={{ fontSize: ".64rem", color: "var(--muted)", fontWeight: 700 }}>
              BOOKED P&L
            </Typography>
            <Typography
              className={`mono ${tone(stats.realised_pnl)}`}
              sx={{ fontSize: "1rem", fontWeight: 800, mt: 0.3 }}
            >
              {money(stats.realised_pnl, true)}
            </Typography>
          </Box>

          <Box className="tile3d" sx={{ flex: "1 1 132px" }}>
            <Typography sx={{ fontSize: ".64rem", color: "var(--muted)", fontWeight: 700 }}>
              UNREALISED
            </Typography>
            <Typography
              className={`mono ${tone(stats.unrealised_pnl)}`}
              sx={{ fontSize: "1rem", fontWeight: 800, mt: 0.3 }}
            >
              {money(stats.unrealised_pnl, true)}
            </Typography>
          </Box>
        </Stack>

        <Stack direction="row" gap={1.2} flexWrap="wrap" sx={{ mt: 1.2 }}>
          <Box className="tile3d" sx={{ flex: "1 1 132px" }}>
            <Typography sx={{ fontSize: ".64rem", color: "var(--muted)", fontWeight: 700 }}>
              CAPITAL DEPLOYED
            </Typography>
            <Typography
              className="mono"
              sx={{ fontSize: "1rem", fontWeight: 800, mt: 0.3, color: "var(--text-2)" }}
            >
              {money(capitalDeployed(open))}
            </Typography>
          </Box>

          <Box className="tile3d" sx={{ flex: "1 1 132px" }}>
            <Typography sx={{ fontSize: ".64rem", color: "var(--muted)", fontWeight: 700 }}>
              ACTIVE ALGOS
            </Typography>
            <Typography
              className="mono"
              sx={{ fontSize: "1rem", fontWeight: 800, mt: 0.3, color: "var(--text-2)" }}
            >
              {activeAlgos}
            </Typography>
          </Box>
        </Stack>

        {/* win-rate bar */}
        <Box sx={{ mt: 2 }}>
          <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.7 }}>
            <Typography sx={{ fontSize: ".68rem", color: "var(--muted)", fontWeight: 700 }}>
              WIN RATE
            </Typography>
            <Typography className="mono" sx={{ fontSize: ".74rem", fontWeight: 800 }}>
              {stats.win_rate.toFixed(1)}%{" "}
              <span style={{ color: "var(--muted)", fontWeight: 600 }}>
                ({stats.wins}W / {stats.losses}L)
              </span>
            </Typography>
          </Stack>
          <Box
            sx={{
              height: 7,
              borderRadius: 4,
              background: "#0a121c",
              overflow: "hidden",
              boxShadow: "inset 0 1px 3px rgba(0,0,0,.7)",
            }}
          >
            <Box
              sx={{
                height: "100%",
                width: `${Math.min(100, Math.max(0, stats.win_rate))}%`,
                background: "linear-gradient(90deg,#0f7a45,#00e676)",
                boxShadow: "0 0 12px rgba(0,230,118,.55)",
                transition: "width 600ms cubic-bezier(.22,1,.36,1)",
              }}
            />
          </Box>
        </Box>
      </Box>
    </Card3D>
  );
}
